import {
	ApolloClient,
	ApolloLink,
	HttpLink,
	InMemoryCache,
	Observable,
	split,
} from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
// import { InMemoryCache } from 'apollo-cache-inmemory';
// import { ApolloLink, Observable, split } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import { createUploadLink } from 'apollo-upload-client';
import { getMainDefinition } from 'apollo-utilities';
import jwtDecode from 'jwt-decode';
import { getAccessToken, setAccessToken } from './AccessToken';
import { useReducer, Reducer } from 'react';

import { createBrowserHistory } from 'history';

export const browserHistory = createBrowserHistory();

// Environment configuration mapping
type EnvironmentConfig = {
	baseUrl: string;
	useWss: boolean;
};

const environmentConfigs: Record<string, EnvironmentConfig> = {
	outlet1: {
		baseUrl: 'https://menuxapi1.menux.asia',
		useWss: true,
	},
	outlet2: {
		baseUrl: 'https://menuxapi2.menux.asia',
		useWss: true,
	},
	pkns: {
		baseUrl: 'https://pos-api.menux.asia',
		useWss: true,
	},
	'pkns-uat': {
		baseUrl: 'https://pos-uat-api.menux.asia',
		useWss: true,	
	},
	swan: {
		baseUrl: 'https://menuxswanapi.menux.asia',
		useWss: true,
	},
	swanuat: {
		baseUrl: 'https://menuxswanapi-uat.menux.asia',
		useWss: true,
	},
	'promenade-staging': {
		baseUrl: 'https://menuxapi-promenade-uat.menux.asia',
		useWss: true,
	},
	promenade: {
		baseUrl: 'https://menuxapi-promenade.menux.asia',
		useWss: true,
	},
	'imperial-staging': {
		baseUrl: 'https://menuxapi-imperial-uat.menux.asia',
		useWss: true,
	},
	imperial: {
		baseUrl: 'https://menuxapi-imperial.menux.asia',
		useWss: true,
	},
	edu: {
		baseUrl: 'https://menuxapi-edu.menux.asia',
		useWss: true,
	},
	local: {
		baseUrl: 'http://localhost:9000',
		useWss: false,
	},
	dev: {
		baseUrl: 'https://posx-dev-api.ifca.io',
		useWss: true,
	},
	uat: {
		baseUrl: 'https://menux-uat-api.menux.asia',
		useWss: true,
	},
};

// Default configuration (production)
const defaultConfig: EnvironmentConfig = {
	baseUrl: 'https://menuxapi.menux.asia',
	useWss: true,
};

// Get current environment configuration
// Supports runtime configuration (window.__APP_ENV__) for "build once, deploy to multiple environments"
// Falls back to build-time configuration (process.env.REACT_APP_ENV) for development
const getEnvConfig = (): EnvironmentConfig => {
	// Priority 1: Build-time environment variable (for development - takes precedence)
	// Priority 2: Runtime configuration (for production deployments)
	// This ensures development mode (yarn local, yarn dev, etc.) always works correctly
	const env = process.env.REACT_APP_ENV || (typeof window !== 'undefined' && window.__APP_ENV__);
	
	// If custom base URL is provided via runtime config, use it directly
	if (typeof window !== 'undefined' && window.__APP_BASE_URL__) {
		const baseUrl = window.__APP_BASE_URL__;
		// Determine if WSS should be used (true for https, false for http/localhost)
		const useWss = baseUrl.startsWith('https://') && !baseUrl.includes('localhost');
		return { baseUrl, useWss };
	}
	
	// Otherwise, use environment-based configuration
	if (env && env in environmentConfigs) {
		return environmentConfigs[env];
	}
	return defaultConfig;
};

// Resolve configuration (runtime config takes precedence, then build-time env)
const config = getEnvConfig();

// Generate URLs from base configuration
const posNodeUrl = `${config.baseUrl}/menux-graphql`;
export const menuxSocketUrl = config.baseUrl;
const wsNodeUrl = `${config.useWss ? 'wss' : 'ws'}://${config.baseUrl.replace(/^https?:\/\//, '')}/menux-graphql`;
export const posNodeRefreshUrl = `${config.baseUrl}/refresh_token_menu`;

const cache = new InMemoryCache({});

//Getting access Token and passing it in request headers
const requestLink = new ApolloLink(
	(operation, forward) =>
		new Observable(observer => {
			let handle: any;
			Promise.resolve(operation)
				.then(operation => {
					const accessToken = getAccessToken();
					if (accessToken) {
						operation.setContext({
							headers: {
								authorization: `bearer ${accessToken}`,
							},
						});
					} //accessToken is defined
				}) //then operation ends here
				.then(() => {
					handle = forward(operation).subscribe({
						next: observer.next.bind(observer),
						error: observer.error.bind(observer),
						complete: observer.complete.bind(observer),
					}); //handle ends here
				})
				.catch(observer.error.bind(observer));

			return () => {
				if (handle) handle.unsubscribe();
			};
		}),
);

const uploadLink = createUploadLink({
	uri: posNodeUrl,
	credentials: 'include',
});

const httplink = new HttpLink({
	uri: posNodeUrl,
	credentials: 'include',
}); //new HttpLink ends here

const wsLink = new WebSocketLink({
	uri: wsNodeUrl,
	options: {
		reconnect: true,
		timeout: 20000,
		lazy: true,
	},
});

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
	({ query }) => {
		const definition = getMainDefinition(query);
		return (
			definition.kind === 'OperationDefinition' &&
			definition.operation === 'subscription'
		);
	},
	wsLink,
	httplink,
);

export const WSclient = new ApolloClient({
	cache: cache,
	link: splitLink,
});

export const POSClient = new ApolloClient({
	link: ApolloLink.from([
		new TokenRefreshLink({
			accessTokenField: 'accessToken',
			isTokenValidOrUndefined: () => {
				const token = getAccessToken();

				if (!token) {
					return true;
				}

				try {
					const { exp } = jwtDecode(token);
					if (Date.now() >= exp * 1000) {
						return false;
					} else {
						return true;
					}
				} catch (err) {
					console.log(err);
					return false;
				}
			},
			fetchAccessToken: () => {
				return fetch(posNodeRefreshUrl, {
					method: 'POST',
					credentials: 'include',
				});
			},
			handleFetch: accessToken => {
				setAccessToken(accessToken);
			},
			handleResponse: () => {},
			handleError: err => {
				console.warn('Your refresh token is invalid. Try to relogin');
				console.error(err);
			},
		}),
		onError(({ graphQLErrors }) => {
			console.log(graphQLErrors, 'graphQLErrors');
			if (
				graphQLErrors[0]?.message
					.toLowerCase()
					.includes('Already login to another device.'.toLowerCase())
			) {
				if (browserHistory?.location?.pathname !== '/login') {
					window.location.href = '/login';
				}
			}
		}),
		requestLink,
		uploadLink as any, // property split as incompatible
		// splitLink,
	]),
	cache,
});
